import React, { useEffect, useMemo, useState } from 'react';
import { Banknote, Building2, Landmark, Truck } from 'lucide-react';
import { fmtMoney, statusDef, normalizeSalesChannel, formatOrderDate } from '../../utils/orderUtils';
import { useToast } from '../ToastManager';
import EntityInspector from '../ui/EntityInspector';
import StatusBadge from '../ui/StatusBadge';
import ChannelBadge from '../ui/ChannelBadge';
import Button from '../ui/Button';
import { OrderItems, OrderTotals, customerName } from './OrderListInspector';
import { propsSoloLectura } from '../../utils/soloLectura';
import '../PendingShipmentRow.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/* ── Drawer de despacho — "Pendientes de envío" (F5 Tema 4) ───────────────────
   El flujo de captura de envío se muda de casa (antes acordeón inline en la fila,
   ahora el drawer compartido del molde) pero NO de lógica: el mismo
   PUT /api/orders/:id/shipping-status escribe Woo, registra el costo real y su
   diferencia, y según la forma de pago crea el egreso en la caja elegida (Caja y
   Bancos) o la cuenta por pagar en Finanzas. Es trabajo, no consulta — vive en el
   drawer igual que Retiros de Inventario (precedente F4.4). */

export function getShippingCharged(order) {
  const lines = order?.shipping_lines || [];
  const lineTotal = lines.reduce((sum, line) => sum + (Number(line.total) || 0), 0);
  return lineTotal || Number(order?.shipping_total) || 0;
}

function addressFromOrder(order) {
  const shipping = order?.shipping || {};
  const billing = order?.billing || {};
  return {
    first_name: shipping.first_name || billing.first_name || '',
    last_name: shipping.last_name || billing.last_name || '',
    address_1: shipping.address_1 || billing.address_1 || '',
    address_2: shipping.address_2 || billing.address_2 || '',
    city: shipping.city || billing.city || '',
    state: shipping.state || billing.state || '',
    postcode: shipping.postcode || billing.postcode || '',
    country: shipping.country || billing.country || 'CO',
  };
}

/* order === null → cerrado. Se monta con `key={order.id}` en el padre: navegar
   con ↑/↓ remonta el drawer y refresca el formulario desde el nuevo pedido. */
export default function ShipmentDispatchDrawer({ order, onClose, onPrev, onNext, onConfirmed, soloLectura = false }) {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [address, setAddress] = useState(() => addressFromOrder(order));
  const [phone, setPhone] = useState(order?.billing?.phone || '');
  const [comment, setComment] = useState(order?.comentario_envio || '');
  const [transportType, setTransportType] = useState('mensajero');
  const [transportName, setTransportName] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const chargedToClient = useMemo(() => getShippingCharged(order), [order]);
  const [realCost, setRealCost] = useState(() => (chargedToClient > 0 ? String(chargedToClient) : ''));
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [cajas, setCajas] = useState([]);
  const [cajaId, setCajaId] = useState('');

  // Cajas operativas para el pago del envío (excluye especiales y cerradas): el egreso
  // queda registrado directo en la caja elegida, conectado con Caja y Bancos.
  useEffect(() => {
    if (!order) return;
    fetch(`${API_URL}/api/cashflow/accounts`)
      .then((r) => (r.ok ? r.json() : { accounts: [] }))
      .then((d) => {
        const usable = (d.accounts || []).filter(
          (a) => a.isActive !== false && a.status !== 'closed' && !a.deletedAt && !a.excluirDeTotalConsolidado
        );
        setCajas(usable);
        setCajaId((prev) => prev || (usable.find((a) => a.type === 'cash') || usable[0])?._id || '');
      })
      .catch(() => setCajas([]));
  }, [order]);

  const realCostNum = Number(realCost);
  const validCost = Number.isFinite(realCostNum) && realCostNum >= 0;
  const diff = validCost ? chargedToClient - realCostNum : 0;
  const noteAmount = validCost && realCostNum > 0 ? fmtMoney(realCostNum) : 'el valor ingresado';

  useEffect(() => {
    if (!order) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const qs = transportName.trim() ? `?q=${encodeURIComponent(transportName.trim())}` : '';
        const res = await fetch(`${API_URL}/api/orders/shipping-transport-suggestions${qs}`, { signal: controller.signal });
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } catch (_) {
        if (!controller.signal.aborted) setSuggestions([]);
      }
    }, 220);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [transportName, order]);

  const updateAddress = (field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfirm = async () => {
    if (!address.address_1.trim()) return setError('Ingresa la dirección de envío.');
    if (!phone.trim()) return setError('Ingresa el teléfono de contacto.');
    if (!transportName.trim()) return setError('Ingresa el nombre del mensajero o empresa.');
    if (!validCost) return setError('Ingresa un costo real de envío válido.');
    if ((paymentMethod === 'efectivo' || paymentMethod === 'pendiente') && realCostNum <= 0) {
      return setError('El costo real debe ser mayor a cero para efectivo o pendiente de pago.');
    }
    if (paymentMethod !== 'pendiente' && cajas.length > 0 && !cajaId) {
      return setError('Elige de qué caja sale el pago al mensajero.');
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/shipping-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shipping_address: address,
          telefono_contacto: phone.trim(),
          comentario_envio: comment.trim(),
          tipo_transporte: transportType,
          nombre_transportista: transportName.trim(),
          costo_envio_real: realCostNum,
          pago_envio_metodo: paymentMethod,
          pago_envio_caja_id: paymentMethod !== 'pendiente' ? cajaId : '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al confirmar el envío');
      showToast({ variant: 'success', message: `Envío del pedido #${order.number} confirmado` });
      if (onConfirmed) onConfirmed(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const transportOptions = [
    { value: 'mensajero', label: 'Mensajero propio', icon: Truck },
    { value: 'empresa', label: 'Empresa de transporte', icon: Building2 },
  ];

  const paymentOptions = [
    { value: 'efectivo', label: 'Le pagué en efectivo' },
    { value: 'transferencia', label: 'Le pagué por transferencia' },
    { value: 'pendiente', label: 'Le quedo debiendo' },
  ];

  return (
    <EntityInspector
      open={!!order}
      mode="drawer"
      title={order ? `Envío del pedido #${order.number}` : ''}
      subtitle={order ? `${formatOrderDate(order.date_created)} · ${customerName(order)}` : ''}
      status={order && <>
        <StatusBadge variant={statusDef(order.status).variant}>{statusDef(order.status).label}</StatusBadge>
        <ChannelBadge channel={normalizeSalesChannel(order).key} />
      </>}
      actions={order && <>
        <Button variant="tertiary" onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="primary" loading={saving} {...propsSoloLectura(soloLectura)} onClick={handleConfirm}>
          {saving ? 'Confirmando…' : 'Confirmar envío'}
        </Button>
      </>}
      onClose={onClose}
      onPrev={onPrev}
      onNext={onNext}
    >
      {order && (
        <div className="op-inspector-body">
          {/* Contexto del pedido (QUÉ → CUÁNTO) antes del formulario de despacho */}
          <OrderItems order={order} />
          <div className="op-ticket"><OrderTotals order={order} /></div>

          <div className="op-ship-drawer-form">
            <section className="psr-section">
              <h4>Datos de envío</h4>
              <div className="psr-grid psr-grid--address">
                <label>
                  <span>Dirección</span>
                  <input value={address.address_1} onChange={(e) => updateAddress('address_1', e.target.value)} placeholder="Calle, carrera, número" />
                </label>
                <label>
                  <span>Apto / referencia</span>
                  <input value={address.address_2} onChange={(e) => updateAddress('address_2', e.target.value)} placeholder="Interior, barrio, referencia" />
                </label>
                <label>
                  <span>Ciudad</span>
                  <input value={address.city} onChange={(e) => updateAddress('city', e.target.value)} placeholder="Ciudad" />
                </label>
                <label>
                  <span>Departamento</span>
                  <input value={address.state} onChange={(e) => updateAddress('state', e.target.value)} placeholder="Departamento" />
                </label>
                <label>
                  <span>Teléfono de contacto</span>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Teléfono" />
                </label>
                <label className="psr-field-wide">
                  <span>Comentario / instrucciones</span>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Opcional" />
                </label>
              </div>
            </section>

            <section className="psr-section">
              <h4>Cómo se envía</h4>
              <div className="psr-segmented">
                {transportOptions.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={transportType === value ? 'psr-segment psr-segment--active' : 'psr-segment'}
                    onClick={() => {
                      setTransportType(value);
                      setTransportName('');
                    }}
                  >
                    <Icon size={16} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
              <label>
                <span>{transportType === 'empresa' ? 'Nombre de la empresa' : 'Nombre del mensajero'}</span>
                <input
                  list={`psr-transport-${order.id}`}
                  value={transportName}
                  onChange={(e) => setTransportName(e.target.value)}
                  placeholder={transportType === 'empresa' ? 'Ej. Servientrega' : 'Ej. Juan'}
                />
                <datalist id={`psr-transport-${order.id}`}>
                  {suggestions.map((item) => <option value={item} key={item} />)}
                </datalist>
              </label>
            </section>

            <section className="psr-section">
              <h4>Pago al mensajero/transportador</h4>
              <div className="psr-cost-grid">
                <div className="psr-reference">
                  <span>Cobrado al cliente por envío</span>
                  <strong>{fmtMoney(chargedToClient)}</strong>
                  <small>Valor de referencia — ya cobrado en el pedido</small>
                </div>
                <label>
                  <span>¿Cuánto le pagas al mensajero/transportador?</span>
                  <input type="number" min="0" step="100" value={realCost} onChange={(e) => setRealCost(e.target.value)} />
                </label>
              </div>
              {validCost && (
                <div className={`psr-diff${diff > 0 ? ' psr-diff--gain' : diff < 0 ? ' psr-diff--loss' : ''}`}>
                  {diff === 0
                    ? 'Diferencia: $0'
                    : diff > 0
                      ? `Diferencia: ${fmtMoney(Math.abs(diff))} a favor de la tienda`
                      : `Diferencia: ${fmtMoney(Math.abs(diff))} asumida por la tienda`}
                </div>
              )}
              <div className="psr-payment-options">
                {paymentOptions.map((option) => (
                  <label key={option.value} className={paymentMethod === option.value ? 'psr-payment psr-payment--active' : 'psr-payment'}>
                    <input type="radio" checked={paymentMethod === option.value} onChange={() => setPaymentMethod(option.value)} />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {paymentMethod !== 'pendiente' && cajas.length > 0 && (
                <div className="psr-caja-picker">
                  <span className="psr-caja-picker__label">¿De qué caja sale el pago?</span>
                  <div className="psr-caja-chips">
                    {cajas.map((a) => {
                      const Icon = a.type === 'cash' ? Banknote : Landmark;
                      return (
                        <button key={a._id} type="button"
                          className={`psr-caja-chip${cajaId === a._id ? ' psr-caja-chip--on' : ''}`}
                          onClick={() => setCajaId(a._id)}>
                          <Icon size={14} /> {a.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className={`psr-payment-note psr-payment-note--${paymentMethod}`}>
                {paymentMethod === 'efectivo' && `Esto descuenta ${noteAmount} de la caja seleccionada, y queda visible en Caja y Bancos.`}
                {paymentMethod === 'transferencia' && `Esto registra un egreso de ${noteAmount} en la caja seleccionada, visible en Caja y Bancos.`}
                {paymentMethod === 'pendiente' && `Se crea una cuenta por pagar de ${noteAmount} en Finanzas. Cuando le pagues, búscala ahí para marcarla como pagada.`}
              </div>
            </section>

            {error && <div className="psr-error">{error}</div>}
          </div>
        </div>
      )}
    </EntityInspector>
  );
}
